/**
 * Pure derivation helpers for the Validation Step Breakdown pivot.
 * Turns a nested `/search-agg` response into a hierarchical
 * `PivotRow` tree plus an ordered `PivotColumn` list, computes
 * per-cell pass rate, color, and display text, and asserts internal
 * shape invariants in debug builds.
 *
 * Free of React, Redux, and DOM references so it can be reused from
 * memoized selectors and unit / property tests.
 */

export type RowGroupLevel =
  | "grandTotal"
  | "productLine"
  | "program"
  | "marketplace"
  | "asinType";

export type CellColor = "green" | "orange" | "red" | "grey";

export type ValidationPipeline = "LVS" | "HATTRICK";

export interface RowGroupPathToken {
  readonly level: RowGroupLevel;
  /**
   * The value used to build filter chips (e.g. a productLineId).
   * Consumers driving `applyStepDrilldownFilters` read this field.
   */
  readonly value: string;
  /**
   * Human-readable label shown in tooltips / aria-labels. Present
   * when the filter value differs from the display label (e.g. an
   * opaque product-line ID that needs a name lookup). Callers that
   * render the path should prefer `displayLabel ?? value`.
   */
  readonly displayLabel?: string;
}

export type RowGroupPath = readonly RowGroupPathToken[];

export interface PivotColumn {
  readonly stepName: string;
  readonly pipeline: ValidationPipeline;
  readonly headerLabel: string;
}

export interface PivotCell {
  readonly totalCount: number;
  readonly passCount: number;
  readonly passRate: number;
  readonly color: CellColor;
  readonly displayText: string;
}

export interface PivotRow {
  readonly path: RowGroupPath;
  readonly level: RowGroupLevel;
  readonly label: string;
  readonly totalCount: number;
  /**
   * Cells keyed by `pivotCellKey(column)` (`"<pipeline>:<stepName>"`).
   * The pipeline prefix is required because `stepName` is not unique
   * across the LVS and Hattrick pipelines.
   */
  readonly cellsByColumnKey: ReadonlyMap<string, PivotCell>;
  readonly children: readonly PivotRow[];
}

/**
 * Composite key for `PivotRow.cellsByColumnKey` and for the
 * `AggregationRowBucket.steps` record produced by the connector.
 * Keying by step name alone would collide when the same step name
 * appears in both pipelines (e.g., both LVS and Hattrick define
 * "Compliance").
 */
export function pivotCellKey(col: {
  readonly pipeline: ValidationPipeline;
  readonly stepName: string;
}): string {
  return `${col.pipeline}:${col.stepName}`;
}

export interface AggregationStepCount {
  readonly passCount: number;
  readonly totalCount: number;
}

export interface AggregationRowBucket {
  readonly level: RowGroupLevel;
  /**
   * Human-readable label rendered in the Group column and in
   * tooltips. For product-line and program buckets this is the
   * resolved name; for country / asinType buckets it matches the
   * raw filter value.
   */
  readonly label: string;
  /**
   * Optional filter value used to build filter chips. Defaults to
   * `label` when absent. For product-line and program buckets this
   * carries the opaque ID that the FilterBar's chip config maps
   * back to a display name.
   */
  readonly filterValue?: string;
  readonly count: number;
  /**
   * Per-step counts keyed by `pivotCellKey({ pipeline, stepName })`
   * (`"<pipeline>:<stepName>"`). Keying by step name alone would
   * collide when both the LVS and Hattrick pipelines expose a step
   * with the same name. Stored as a plain object so the whole
   * response tree stays serializable inside Redux state.
   */
  readonly steps?: Readonly<Record<string, AggregationStepCount>>;
  readonly children?: readonly AggregationRowBucket[];
}

export interface StepBreakdownAggregationResponse {
  readonly totalCount: number;
  readonly rows: readonly AggregationRowBucket[];
  readonly steps: readonly PivotColumn[];
}

const GRAND_TOTAL_LABEL = "Grand Total";
const EM_DASH = "\u2014";
const DIGIT_GROUPING_THRESHOLD = 999;

const PIPELINE_LABEL_PREFIX: Record<ValidationPipeline, string> = {
  LVS: "",
  HATTRICK: "",
};

const digitGroupFormatter = new Intl.NumberFormat();

function isDebugBuild(): boolean {
  const g = globalThis as {
    readonly process?: { readonly env?: { readonly NODE_ENV?: string } };
  };
  return g.process?.env?.NODE_ENV !== "production";
}

/**
 * Reorder columns so LVS steps come first, then Hattrick steps,
 * preserving each pipeline's incoming order. Each column's
 * `headerLabel` is derived from `pipeline + stepName` so callers
 * don't need to prefix headers themselves.
 */
function orderColumns(input: readonly PivotColumn[]): readonly PivotColumn[] {
  const lvs: PivotColumn[] = [];
  const hattrick: PivotColumn[] = [];
  for (const col of input) {
    const normalized: PivotColumn = {
      stepName: col.stepName,
      pipeline: col.pipeline,
      headerLabel: `${PIPELINE_LABEL_PREFIX[col.pipeline]}${col.stepName}`,
    };
    if (col.pipeline === "LVS") {
      lvs.push(normalized);
    } else {
      hattrick.push(normalized);
    }
  }
  return [...lvs, ...hattrick];
}

function compareCaseInsensitive(a: string, b: string): number {
  return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
}

function formatCount(n: number, useGrouping: boolean): string {
  return useGrouping ? digitGroupFormatter.format(n) : String(n);
}

/**
 * Returns the color bucket for a cell. Decisions use the raw
 * unrounded `passRate` so that a cell rounding to `100%` while
 * `passCount < totalCount` still renders orange rather than green.
 */
export function getCellColor(passRate: number, totalCount: number): CellColor {
  if (totalCount === 0) {
    return "grey";
  }
  if (passRate === 100) {
    return "green";
  }
  if (passRate >= 80) {
    return "orange";
  }
  return "red";
}

/**
 * Renders the visible cell text: the em-dash glyph for zero-total
 * cells, otherwise `"P% (X/Y)"` with locale digit grouping applied
 * to `X` and `Y` when either count exceeds three digits. `P` is the
 * integer percentage rounded half-up.
 */
export function renderCellText(cell: PivotCell): string {
  if (cell.totalCount === 0) {
    return EM_DASH;
  }
  const rounded = Math.round(cell.passRate);
  const useGrouping =
    cell.passCount > DIGIT_GROUPING_THRESHOLD ||
    cell.totalCount > DIGIT_GROUPING_THRESHOLD;
  const x = formatCount(cell.passCount, useGrouping);
  const y = formatCount(cell.totalCount, useGrouping);
  return `${rounded}% (${x}/${y})`;
}

/**
 * Given a stack of aggregation buckets from the outermost group to
 * the current row, returns the ordered `RowGroupPath` used to
 * identify the row for drill-down clicks and expand/collapse keys.
 */
export function computeRowGroupPathTokens(
  ancestors: readonly AggregationRowBucket[]
): RowGroupPath {
  return ancestors.map<RowGroupPathToken>((b) => {
    if (b.filterValue !== undefined && b.filterValue !== b.label) {
      return {
        level: b.level,
        value: b.filterValue,
        displayLabel: b.label,
      };
    }
    return { level: b.level, value: b.label };
  });
}

function makeCell(passCount: number, totalCount: number): PivotCell {
  const passRate = totalCount === 0 ? 0 : (passCount / totalCount) * 100;
  const color = getCellColor(passRate, totalCount);
  const preliminary: PivotCell = {
    totalCount,
    passCount,
    passRate,
    color,
    displayText: "",
  };
  const displayText = renderCellText(preliminary);
  return { ...preliminary, displayText };
}

function buildCellsFromBucket(
  steps: Readonly<Record<string, AggregationStepCount>> | undefined,
  columns: readonly PivotColumn[]
): ReadonlyMap<string, PivotCell> {
  const cells = new Map<string, PivotCell>();
  for (const col of columns) {
    const key = pivotCellKey(col);
    const entry = steps?.[key];
    const passCount = entry?.passCount ?? 0;
    const totalCount = entry?.totalCount ?? 0;
    cells.set(key, makeCell(passCount, totalCount));
  }
  return cells;
}

function aggregateCellsFromChildren(
  children: readonly PivotRow[],
  columns: readonly PivotColumn[]
): ReadonlyMap<string, PivotCell> {
  const cells = new Map<string, PivotCell>();
  for (const col of columns) {
    const key = pivotCellKey(col);
    let passCount = 0;
    let totalCount = 0;
    for (const child of children) {
      const childCell = child.cellsByColumnKey.get(key);
      if (childCell !== undefined) {
        passCount += childCell.passCount;
        totalCount += childCell.totalCount;
      }
    }
    cells.set(key, makeCell(passCount, totalCount));
  }
  return cells;
}

function buildRow(
  bucket: AggregationRowBucket,
  ancestors: readonly AggregationRowBucket[],
  columns: readonly PivotColumn[]
): PivotRow {
  const nextAncestors: readonly AggregationRowBucket[] = [...ancestors, bucket];
  const path = computeRowGroupPathTokens(nextAncestors);

  const rawChildren = bucket.children ?? [];
  if (rawChildren.length === 0) {
    return {
      path,
      level: bucket.level,
      label: bucket.label,
      totalCount: bucket.count,
      cellsByColumnKey: buildCellsFromBucket(bucket.steps, columns),
      children: [],
    };
  }

  const childRows = rawChildren
    .map((c) => buildRow(c, nextAncestors, columns))
    .sort((a, b) => compareCaseInsensitive(a.label, b.label));

  return {
    path,
    level: bucket.level,
    label: bucket.label,
    totalCount: bucket.count,
    cellsByColumnKey: aggregateCellsFromChildren(childRows, columns),
    children: childRows,
  };
}

/**
 * Recursively return a copy of `row` whose `cellsByColumnKey` retains
 * only the keys in `keptKeys`. Row totals and children are preserved;
 * only the per-column cells are filtered, so a column dropped as
 * entirely-N/A disappears from every level of the tree at once.
 */
function retainColumns(row: PivotRow, keptKeys: ReadonlySet<string>): PivotRow {
  const cells = new Map<string, PivotCell>();
  for (const [key, cell] of row.cellsByColumnKey) {
    if (keptKeys.has(key)) {
      cells.set(key, cell);
    }
  }
  return {
    ...row,
    cellsByColumnKey: cells,
    children: row.children.map((c) => retainColumns(c, keptKeys)),
  };
}

/**
 * Walks the nested aggregation response, sorts sibling rows
 * case-insensitive alphabetical by label, aggregates per-step counts
 * from leaves up to each parent, and synthesizes a `Grand_Total_Row`
 * at the top whose `totalCount` reflects `response.totalCount` and
 * whose per-step cells are the sum of top-level rows' cells.
 *
 * A step column whose applicable total (pass + pass_exception + fail)
 * is zero across every row is dropped entirely: such a step is
 * `not_applicable` for the whole filtered set, so showing it as
 * `0% (0/0)` would be noise. Because each cell's `totalCount` is the
 * applicable count (the connector maps the `applicableCount` filter,
 * not the raw step `documentCount`), the grand-total cell's
 * `totalCount` is exactly this per-column sum.
 *
 * Returns the ordered `columns` list (LVS first, then Hattrick) and
 * the synthesized `grandTotal` row. In debug builds, validates the
 * derived tree against the shape invariants documented on
 * `PivotRow` and `PivotCell`.
 */
export function buildPivotFromAggregation(
  response: StepBreakdownAggregationResponse
): {
  readonly grandTotal: PivotRow;
  readonly columns: readonly PivotColumn[];
} {
  const allColumns = orderColumns(response.steps);

  const topLevelRows = response.rows
    .map((r) => buildRow(r, [], allColumns))
    .sort((a, b) => compareCaseInsensitive(a.label, b.label));

  const fullGrandTotal: PivotRow = {
    path: [],
    level: "grandTotal",
    label: GRAND_TOTAL_LABEL,
    totalCount: response.totalCount,
    cellsByColumnKey: aggregateCellsFromChildren(topLevelRows, allColumns),
    children: topLevelRows,
  };

  // Keep only columns with a non-zero applicable total across all rows.
  // The grand-total cell already sums every row, so its per-column
  // `totalCount` is the whole-set applicable count for that step. When
  // there are no rows at all (empty result set) nothing is dropped —
  // there is no data on which to judge a step N/A, and callers rely on
  // every requested column surviving the empty state.
  const hasRows = topLevelRows.length > 0;
  const columns = hasRows
    ? allColumns.filter((col) => {
        const cell = fullGrandTotal.cellsByColumnKey.get(pivotCellKey(col));
        return cell !== undefined && cell.totalCount > 0;
      })
    : allColumns;

  const keptKeys = new Set<string>(columns.map((c) => pivotCellKey(c)));
  const grandTotal =
    keptKeys.size === fullGrandTotal.cellsByColumnKey.size
      ? fullGrandTotal
      : retainColumns(fullGrandTotal, keptKeys);

  if (isDebugBuild()) {
    assertPivotInvariants(grandTotal);
  }

  return { grandTotal, columns };
}

/**
 * Recursively validates the derived pivot tree. Only runs outside
 * production builds. Throws on the first violation so bugs surface
 * loud during development rather than silently rendering bad totals.
 */
function assertPivotInvariants(row: PivotRow): void {
  if (!Number.isFinite(row.totalCount) || row.totalCount < 0) {
    throw new Error(
      `stepBreakdown: PivotRow.totalCount must be a finite non-negative number, got ${row.totalCount} at "${row.label}"`
    );
  }

  for (const [columnKey, cell] of row.cellsByColumnKey) {
    if (
      !Number.isFinite(cell.passCount) ||
      cell.passCount < 0 ||
      cell.passCount > cell.totalCount
    ) {
      throw new Error(
        `stepBreakdown: PivotCell.passCount must be in [0, totalCount], got ${cell.passCount}/${cell.totalCount} at "${row.label}" / "${columnKey}"`
      );
    }
    if (cell.totalCount > 0) {
      if (
        !Number.isFinite(cell.passRate) ||
        cell.passRate < 0 ||
        cell.passRate > 100
      ) {
        throw new Error(
          `stepBreakdown: PivotCell.passRate must be in [0, 100], got ${cell.passRate} at "${row.label}" / "${columnKey}"`
        );
      }
    }
  }

  if (row.children.length > 0) {
    for (const [columnKey, parentCell] of row.cellsByColumnKey) {
      let childPass = 0;
      let childTotal = 0;
      for (const child of row.children) {
        const childCell = child.cellsByColumnKey.get(columnKey);
        if (childCell !== undefined) {
          childPass += childCell.passCount;
          childTotal += childCell.totalCount;
        }
      }
      if (
        childPass !== parentCell.passCount ||
        childTotal !== parentCell.totalCount
      ) {
        throw new Error(
          `stepBreakdown: parent-equals-sum-of-children violated at "${row.label}" / "${columnKey}": parent=${parentCell.passCount}/${parentCell.totalCount}, children=${childPass}/${childTotal}`
        );
      }
    }
    for (const child of row.children) {
      assertPivotInvariants(child);
    }
  }
}
